/**
 * VAL-FILE-SPLIT-1 — split out of ValuationIntake.tsx under
 * docs/FILE_SIZE_POLICY.md. The four input cards (Earnings, Build-up rate,
 * Market comparables, Asset floor) as presentational components; state,
 * effects, handleRun and draft hydration stay on the page.
 */
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { ValuationMethodology } from '@/types/valuation'
import { basisLabel, money } from './_shared'
import { CsrpGuide } from './_CsrpGuide'
import { replacementCompensationRequired } from './_valuationIntakeHelpers'

export function Field({
  id, label, value, onChange, placeholder, hint, type = 'number',
}: {
  id: string
  label: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
  hint?: string
  type?: string
}) {
  return (
    <div>
      <Label htmlFor={id} className="text-xs">{label}</Label>
      <Input
        id={id}
        type={type}
        step="any"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
      />
      {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
    </div>
  )
}

export function IntakeLegsSection({
  methodology, industry,
  normalizedEarnings, setNormalizedEarnings,
  replacementCompensation, setReplacementCompensation,
  baseCashFlow, setBaseCashFlow,
  riskFree, setRiskFree, startingNote = () => undefined,
  companySpecificRisk, setCompanySpecificRisk,
  equityRiskPremium, setEquityRiskPremium,
  sizePremium, setSizePremium,
  longTermGrowth, setLongTermGrowth,
  growthRate, setGrowthRate,
  projectionYears, setProjectionYears,
  capWeight, setCapWeight,
  compMultiples, setCompMultiples,
  totalAssets, setTotalAssets,
  totalLiabilities, setTotalLiabilities,
  fairValueAdjustments, setFairValueAdjustments,
}: {
  methodology: ValuationMethodology
  industry?: string | null
  normalizedEarnings: string
  setNormalizedEarnings: (v: string) => void
  replacementCompensation: string
  setReplacementCompensation: (v: string) => void
  baseCashFlow: string
  setBaseCashFlow: (v: string) => void
  riskFree: string
  /** Stored source + as-of date for a field's house starting value, if it has one. */
  startingNote?: (key: string, current: string) => string | undefined
  setRiskFree: (v: string) => void
  companySpecificRisk: string
  setCompanySpecificRisk: (v: string) => void
  equityRiskPremium: string
  setEquityRiskPremium: (v: string) => void
  sizePremium: string
  setSizePremium: (v: string) => void
  longTermGrowth: string
  setLongTermGrowth: (v: string) => void
  growthRate: string
  setGrowthRate: (v: string) => void
  projectionYears: string
  setProjectionYears: (v: string) => void
  capWeight: string
  setCapWeight: (v: string) => void
  compMultiples: string
  setCompMultiples: (v: string) => void
  totalAssets: string
  setTotalAssets: (v: string) => void
  totalLiabilities: string
  setTotalLiabilities: (v: string) => void
  fairValueAdjustments: string
  setFairValueAdjustments: (v: string) => void
}) {
  const crossover = methodology.earnings_basis_rule.crossover_earnings_threshold

  return (
    <>
      <Card>
        <CardHeader><CardTitle>Earnings</CardTitle></CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <Field
            id="normalized_earnings" label="Normalized earnings *"
            value={normalizedEarnings} onChange={setNormalizedEarnings}
            placeholder="1200000"
            hint={`Signed SDE↔EBITDA crossover is ${money(crossover)} — below it the basis is ${basisLabel(String(methodology.earnings_basis_rule.basis_below_threshold))}, at or above it ${basisLabel(String(methodology.earnings_basis_rule.basis_at_or_above_threshold))}.`}
          />
          <Field
            id="replacement_compensation"
            label={replacementCompensationRequired(normalizedEarnings, crossover) ? 'Replacement owner compensation *' : 'Replacement owner compensation'}
            value={replacementCompensation} onChange={setReplacementCompensation}
            placeholder="120000"
            hint="What it would cost to hire someone to do the owner's job, per year. Deducted before the income approach capitalizes earnings."
          />
          <Field
            id="base_cash_flow" label="Base cash flow (DCF)"
            value={baseCashFlow} onChange={setBaseCashFlow}
            hint="Blank uses normalized earnings."
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Build-up rate</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-gray-500">
            Percent scale — enter 4.35 for 4.35%. Leave the Kroll inputs blank to use the
            signed Kroll Cost of Capital Navigator defaults.
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field id="risk_free_rate" label="Risk-free rate * (20-yr Treasury)" value={riskFree} onChange={setRiskFree} hint={startingNote('risk_free_rate', riskFree)} />
            <Field id="company_specific_risk" label="Company-specific risk *" value={companySpecificRisk} onChange={setCompanySpecificRisk} hint={startingNote('company_specific_risk', companySpecificRisk)} />
            <CsrpGuide industry={industry} value={companySpecificRisk} />
            <Field id="equity_risk_premium" label="Equity risk premium" value={equityRiskPremium} onChange={setEquityRiskPremium} hint="Blank = Kroll ERP." />
            <Field id="size_premium" label="Size premium" value={sizePremium} onChange={setSizePremium} hint="Blank = Kroll Decile 10 constant (a documented floor, not fitted to this company's size)." />
            <Field id="long_term_growth_rate" label="Long-term growth rate *" value={longTermGrowth} onChange={setLongTermGrowth} hint={["Must not exceed long-run GDP growth — Valuation Signer judgment.", startingNote('long_term_growth_rate', longTermGrowth)].filter(Boolean).join(' ')} />
            <Field id="growth_rate" label="DCF growth rate *" value={growthRate} onChange={setGrowthRate} hint={startingNote('growth_rate', growthRate)} />
            <Field id="projection_years" label="Projection years" value={projectionYears} onChange={setProjectionYears} hint={startingNote('projection_years', projectionYears)} />
            <Field
              id="capitalized_earnings_weight" label="Capitalized-earnings weight"
              value={capWeight} onChange={setCapWeight}
              hint={['Fraction. Balance goes to the DCF leg within the income approach.', startingNote('capitalized_earnings_weight', capWeight)].filter(Boolean).join(' ')}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Market comparables</CardTitle></CardHeader>
        <CardContent>
          <Field
            id="comp_multiples" label="Comparable multiples" type="text"
            value={compMultiples} onChange={setCompMultiples}
            placeholder="3.1, 3.6, 4.0"
            hint="Comma-separated, and optional. Left blank, the signed industry profile supplies the market leg from its own multiple band — which is the normal path. Entered comps are shown as evidence beside the signed range; they never change the value — use Override with a reason to change the multiple."
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Asset floor</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-gray-500">
            Optional. Supplied together, these clamp the low end of the band; omitted, the
            floor leg is simply not applied.
          </p>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field id="total_assets" label="Total assets" value={totalAssets} onChange={setTotalAssets} />
            <Field id="total_liabilities" label="Total liabilities" value={totalLiabilities} onChange={setTotalLiabilities} />
            <Field id="fair_value_adjustments" label="Fair-value adjustments" value={fairValueAdjustments} onChange={setFairValueAdjustments} />
          </div>
        </CardContent>
      </Card>
    </>
  )
}
